import { Sparkles, Crown, Briefcase, AlertTriangle, Compass, ArrowRight, Wrench, MessageSquare, BarChart3 } from "lucide-react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from "recharts";

const LEV_COLOR = { high: "#C0563A", medium: "#1F6E73", low: "#9AAFAE" };
const SCORE_TO_NUM = { high: 3, medium: 2, low: 1 };
const NUM_TO_SCORE = { 3: "High", 2: "Medium", 1: "Low" };

function LeverageBarChart({ data }) {
    // Map leverage_score → numeric so bars have height
    const rows = (data || [])
        .map((r) => ({
            name: (r.title || "").slice(0, 28),
            full: r.title,
            leverage: SCORE_TO_NUM[(r.leverage_score || "low").toLowerCase()] || 1,
            urgency: SCORE_TO_NUM[(r.urgency || "low").toLowerCase()] || 1,
            color: LEV_COLOR[(r.leverage_score || "low").toLowerCase()] || LEV_COLOR.low,
        }))
        .sort((a, b) => b.leverage - a.leverage)
        .slice(0, 8);
    if (rows.length === 0) return null;
    return (
        <div className="bl-card p-5 mt-4" data-testid="leverage-chart">
            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-3">
                Leverage distribution
            </div>
            <ResponsiveContainer width="100%" height={Math.max(240, rows.length * 38)}>
                <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 28, top: 4, bottom: 4 }}>
                    <CartesianGrid horizontal={false} stroke="rgba(0,0,0,0.06)" />
                    <XAxis
                        type="number" domain={[0, 3]} ticks={[1, 2, 3]}
                        tickFormatter={(v) => NUM_TO_SCORE[v]}
                        tick={{ fontSize: 11, fill: "#66707A" }} axisLine={false} tickLine={false}
                    />
                    <YAxis
                        dataKey="name" type="category" width={150}
                        tick={{ fontSize: 11, fill: "#1F2428" }} axisLine={false} tickLine={false}
                    />
                    <Tooltip
                        cursor={{ fill: "rgba(31,110,115,0.06)" }}
                        contentStyle={{
                            background: "#FFFFFF", border: "1px solid #E5E0D8",
                            borderRadius: 8, fontSize: 12,
                        }}
                        formatter={(value) => [NUM_TO_SCORE[value], "Leverage"]}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.full || ""}
                    />
                    <Bar dataKey="leverage" radius={[0, 4, 4, 0]}>
                        {rows.map((r, i) => <Cell key={i} fill={r.color} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function ExposureBreakdownChart({ breakdown }) {
    if (!breakdown) return null;
    const pri = breakdown.priority || {};
    const def = breakdown.deferred || {};
    const opt = breakdown.optional || {};
    const data = [
        { name: "Priority", low: pri.low || 0, mid: (pri.high || 0) - (pri.low || 0), color: "#C0563A" },
        { name: "Deferred", low: def.low || 0, mid: (def.high || 0) - (def.low || 0), color: "#1F6E73" },
        { name: "Optional", low: opt.low || 0, mid: (opt.high || 0) - (opt.low || 0), color: "#9AAFAE" },
    ];
    const maxV = Math.max(
        (pri.high || 0), (def.high || 0), (opt.high || 0), 1,
    );
    if (maxV <= 1) return null;
    return (
        <div className="bl-card p-5 mt-4" data-testid="exposure-chart">
            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-3">
                Exposure distribution (low → high band)
            </div>
            <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data} layout="vertical" margin={{ left: 8, right: 28, top: 4, bottom: 4 }}>
                    <CartesianGrid horizontal={false} stroke="rgba(0,0,0,0.06)" />
                    <XAxis
                        type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                        tick={{ fontSize: 11, fill: "#66707A" }} axisLine={false} tickLine={false}
                    />
                    <YAxis
                        dataKey="name" type="category" width={92}
                        tick={{ fontSize: 12, fill: "#1F2428" }} axisLine={false} tickLine={false}
                    />
                    <Tooltip
                        cursor={{ fill: "rgba(31,110,115,0.06)" }}
                        contentStyle={{
                            background: "#FFFFFF", border: "1px solid #E5E0D8",
                            borderRadius: 8, fontSize: 12,
                        }}
                        formatter={(v, key, p) => {
                            if (key === "low") return [`$${(v / 1000).toFixed(1)}k`, "Low"];
                            const high = (p?.payload?.low || 0) + v;
                            return [`$${(high / 1000).toFixed(1)}k`, "High"];
                        }}
                    />
                    <Bar dataKey="low" stackId="x" radius={[4, 0, 0, 4]}>
                        {data.map((r, i) => <Cell key={i} fill={r.color} fillOpacity={0.85} />)}
                    </Bar>
                    <Bar dataKey="mid" stackId="x" radius={[0, 4, 4, 0]}>
                        {data.map((r, i) => <Cell key={i} fill={r.color} fillOpacity={0.35} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

/**
 * Premium-only intelligence rendering — Closing Strategy Report.
 *
 * Receives the top-level `closing_strategy` object returned from the AI,
 * which contains:
 *   - leverage_scores[]
 *   - seller_strategy[]
 *   - issue_groups[]
 *   - transaction_risk[]
 *   - questions_for_contractor[]
 *   - questions_for_seller[]
 *   - contingency_timing[]
 *   - next_step_roadmap[]
 *
 * Renders nothing when the section is missing — safe for Standard reports.
 */
function ScorePill({ value }) {
    const v = (value || "").toLowerCase();
    const tone =
        v === "high" ? "bl-badge bl-badge-high" :
        v === "low"  ? "bl-badge bl-badge-low"  :
        "bl-badge bl-badge-medium";
    return <span className={`${tone} text-[10px] uppercase tracking-wider`}>{value || "—"}</span>;
}

function SectionHeader({ icon: Icon, eyebrow, title, subtitle }) {
    return (
        <div className="mb-5">
            <div className="bl-eyebrow w-fit flex items-center gap-1.5 text-bl-primary">
                <Icon className="w-3.5 h-3.5" /> {eyebrow}
            </div>
            <h3 className="mt-2 font-display font-semibold text-2xl md:text-3xl text-bl tracking-tight">
                {title}
            </h3>
            {subtitle && <p className="mt-2 text-bl-muted text-sm leading-relaxed max-w-2xl">{subtitle}</p>}
        </div>
    );
}

export default function PremiumIntelligence({ data }) {
    const cs = data?.closing_strategy;
    if (!cs) return null;

    const leverage = cs.leverage_scores || [];
    const strategy = cs.seller_strategy || [];
    const groups   = cs.issue_groups || [];
    const risks    = cs.transaction_risk || [];
    const askContractor = cs.questions_for_contractor || [];
    const askSeller     = cs.questions_for_seller || [];
    const timing   = cs.contingency_timing || [];
    const roadmap  = cs.next_step_roadmap || [];
    const breakdown = data?.exposure_breakdown || null;

    return (
        <div className="space-y-12" data-testid="premium-intelligence">
            {/* Premium hero banner + exposure chart */}
            <div data-testid="premium-banner-wrap">
                <div
                    className="bl-card p-6 sm:p-8 bl-grad-teal-tint-medium border-bl-primary/30"
                    data-testid="premium-banner"
                >
                    <div className="bl-eyebrow w-fit flex items-center gap-1.5">
                        <Crown className="w-3.5 h-3.5 text-bl-primary" /> Closing Strategy Report
                    </div>
                    <h2 className="mt-2 font-display font-semibold text-2xl md:text-3xl text-bl tracking-tight">
                        Negotiation intelligence layer
                    </h2>
                    <p className="mt-2 text-bl-muted text-sm leading-relaxed max-w-2xl">
                        Strategic analysis built on the same findings — designed to help you decide what to ask for, how to sequence it, and where you have leverage.
                    </p>
                </div>
                <ExposureBreakdownChart breakdown={breakdown} />
            </div>

            {/* 1. Leverage scoring */}
            {leverage.length > 0 && (
                <section data-testid="leverage-section">
                    <SectionHeader
                        icon={Sparkles}
                        eyebrow="Negotiation leverage"
                        title="Where you have leverage"
                        subtitle="Each issue is scored for leverage, urgency, and the likelihood the seller pushes back."
                    />
                    <LeverageBarChart data={leverage} />
                    <div className="bl-card overflow-x-auto mt-3">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left">
                                    {["Issue", "Leverage", "Urgency", "Seller pushback", "Why"].map((h) => (
                                        <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {leverage.map((row, i) => (
                                    <tr key={row.issue_id || i} className="border-b border-bl last:border-0">
                                        <td className="px-4 py-3 text-bl font-medium">{row.title}</td>
                                        <td className="px-4 py-3"><ScorePill value={row.leverage_score} /></td>
                                        <td className="px-4 py-3"><ScorePill value={row.urgency} /></td>
                                        <td className="px-4 py-3"><ScorePill value={row.seller_pushback_risk} /></td>
                                        <td className="px-4 py-3 text-bl-muted text-[13px] max-w-md">{row.rationale}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* 2. Seller concession strategy */}
            {strategy.length > 0 && (
                <section data-testid="seller-strategy-section">
                    <SectionHeader
                        icon={Briefcase}
                        eyebrow="Seller strategy"
                        title="Suggested concession approaches"
                        subtitle="Common buyer approaches to each major issue. These are positioning ideas — not legal or transactional advice."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {strategy.map((s, i) => (
                            <div key={i} className="bl-card p-5">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                                    Approach · <span className="text-bl-primary">{(s.approach || "").replaceAll("_", " ")}</span>
                                </div>
                                <div className="mt-2 font-display font-semibold text-bl">
                                    {s.suggested_ask}
                                </div>
                                <p className="mt-2 text-sm text-bl-muted leading-relaxed">{s.rationale}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* 3. Strategic issue groups */}
            {groups.length > 0 && (
                <section data-testid="issue-groups-section">
                    <SectionHeader
                        icon={Sparkles}
                        eyebrow="Strategic grouping"
                        title="Related issues, combined impact"
                        subtitle="Issues that may share root causes or contractor scope, presented together."
                    />
                    <div className="space-y-3">
                        {groups.map((g, i) => (
                            <div key={i} className="bl-card p-5">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="font-display font-semibold text-bl">{g.group_name}</div>
                                    <span className="bl-badge bl-badge-neutral text-[10px]">{g.category}</span>
                                </div>
                                <p className="mt-2 text-sm text-bl leading-relaxed">{g.combined_negotiation_impact}</p>
                                {g.issue_ids?.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {g.issue_ids.map((id) => (
                                            <span key={id} className="font-mono-bl text-[10px] text-bl-muted">{id}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* 4. Transaction risk */}
            {risks.length > 0 && (
                <section data-testid="transaction-risk-section">
                    <SectionHeader
                        icon={AlertTriangle}
                        eyebrow="Transaction risk"
                        title="Financing, insurance & specialist signals"
                        subtitle="Items that may invite additional lender, insurer, or specialist review."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {risks.map((r, i) => (
                            <div
                                key={i}
                                className={`bl-card p-4 border-l-2 ${
                                    r.level === "high" ? "border-l-[var(--bl-rust)]"
                                    : r.level === "medium" ? "border-l-bl-primary/60"
                                    : "border-l-bl-soft"
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="bl-badge bl-badge-neutral text-[10px] capitalize">{r.type}</span>
                                    <ScorePill value={r.level} />
                                </div>
                                <p className="text-sm text-bl leading-relaxed">{r.note}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* 5. Next-step roadmap */}
            {roadmap.length > 0 && (
                <section data-testid="next-step-roadmap-section">
                    <SectionHeader
                        icon={Compass}
                        eyebrow="Action roadmap"
                        title="Recommended next steps, in order"
                        subtitle="A buyer action roadmap based on the findings — review with your agent or attorney before acting."
                    />
                    <ol className="space-y-3">
                        {roadmap.sort((a, b) => (a.step || 0) - (b.step || 0)).map((step, i) => (
                            <li key={i} className="bl-card p-4 flex gap-4">
                                <div className="w-7 h-7 rounded-full bg-bl-teal-tint text-bl-primary font-mono-bl text-sm flex items-center justify-center flex-shrink-0">
                                    {step.step || (i + 1)}
                                </div>
                                <div className="flex-1">
                                    <div className="font-display font-semibold text-bl flex items-center gap-2">
                                        {step.action}
                                        {step.before_close && (
                                            <span className="bl-badge bl-badge-medium text-[10px]">Before close</span>
                                        )}
                                    </div>
                                    {step.rationale && (
                                        <p className="mt-1 text-sm text-bl-muted leading-relaxed">{step.rationale}</p>
                                    )}
                                </div>
                                <ArrowRight className="w-4 h-4 text-bl-muted flex-shrink-0 self-center" />
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            {/* 6. Questions for contractor & seller */}
            {(askContractor.length > 0 || askSeller.length > 0) && (
                <section data-testid="questions-section">
                    <SectionHeader
                        icon={MessageSquare}
                        eyebrow="Question prep"
                        title="What to ask"
                        subtitle="Bring these into your conversations. Calmer questions tend to surface more useful information."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {askContractor.length > 0 && (
                            <div className="bl-card p-5" data-testid="questions-contractor">
                                <div className="bl-eyebrow w-fit flex items-center gap-1.5">
                                    <Wrench className="w-3.5 h-3.5" /> For contractors
                                </div>
                                <ul className="mt-3 space-y-2.5">
                                    {askContractor.map((q, i) => (
                                        <li key={i} className="text-bl text-sm leading-relaxed">• {q}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {askSeller.length > 0 && (
                            <div className="bl-card p-5" data-testid="questions-seller">
                                <div className="bl-eyebrow w-fit flex items-center gap-1.5">
                                    <MessageSquare className="w-3.5 h-3.5" /> For the seller
                                </div>
                                <ul className="mt-3 space-y-2.5">
                                    {askSeller.map((q, i) => (
                                        <li key={i} className="text-bl text-sm leading-relaxed">• {q}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* 7. Contingency timing */}
            {timing.length > 0 && (
                <section data-testid="contingency-section">
                    <SectionHeader
                        icon={AlertTriangle}
                        eyebrow="Contingency & timing"
                        title="What to confirm before the deadline"
                        subtitle="Items commonly investigated before contingency removal."
                    />
                    <div className="space-y-2">
                        {timing.map((t, i) => (
                            <div key={i} className="bl-card p-4 flex items-start gap-3">
                                <span className={`bl-badge text-[10px] ${t.before_deadline ? "bl-badge-medium" : "bl-badge-neutral"}`}>
                                    {t.before_deadline ? "Before deadline" : "Anytime"}
                                </span>
                                <p className="text-sm text-bl leading-relaxed flex-1">{t.note}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
